import { SprintSummaryDrawer } from '@/components/SprintSummaryDrawer';
import { EmptyState, ErrorNotice, Spinner } from '@/components/ui';
import { useProjects, useSprintHistory } from '@/hooks/queries';
import { formatDateRange } from '@/lib/design';
import type { SprintHistoryEntry } from '@/types/api';
import { useEffect, useState } from 'react';

/**
 * Historial de sprints cerrados. Lo ve cualquiera, pero solo alcanza los
 * proyectos a los que pertenece: el historial de un proyecto ajeno revelaria en
 * que trabaja el resto de la organizacion.
 */
export function SprintHistoryPage() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [projectId, setProjectId] = useState('');
  const [openSprintId, setOpenSprintId] = useState<string | null>(null);

  // La busqueda va al servidor, asi que se espera a que se deje de escribir.
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const { data: projects } = useProjects();
  const { data: sprints, isLoading, error, refetch } = useSprintHistory(debouncedSearch, projectId);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-6 py-5">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar…"
          aria-label="Buscar sprints"
          className="w-64 rounded-control border border-line-strong bg-surface px-3 py-2 text-[13.5px] outline-none focus:border-brand"
        />
        <select
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          aria-label="Filtrar por proyecto"
          className="rounded-control border border-line-strong bg-surface px-3 py-2 text-[13.5px] outline-none focus:border-brand"
        >
          <option value="">Todos</option>
          {projects?.map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>

        {sprints && sprints.length > 0 && (
          <p className="ml-auto text-[12.5px] text-subtle">
            {sprints.length} {sprints.length === 1 ? 'sprint cerrado' : 'sprints cerrados'}
          </p>
        )}
      </div>

      {isLoading && (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      )}

      {error && (
        <div className="flex flex-col items-start gap-3">
          <ErrorNotice>No se pudo cargar el historial: {(error as Error).message}</ErrorNotice>
          <button
            type="button"
            onClick={() => void refetch()}
            className="rounded-control border border-line-strong px-3 py-1.5 text-[13px] font-semibold transition-colors hover:border-line-hover"
          >
            Reintentar
          </button>
        </div>
      )}

      {sprints?.length === 0 && (
        <EmptyState
          title={
            debouncedSearch || projectId
              ? 'Ningun sprint coincide con el filtro'
              : 'Todavia no hay sprints cerrados'
          }
          hint={
            debouncedSearch || projectId
              ? undefined
              : 'Al completar un sprint aparece aqui con sus cifras.'
          }
        />
      )}

      <div className="flex flex-col gap-3">
        {sprints?.map((sprint) => (
          <SprintCard
            key={sprint.id}
            sprint={sprint}
            onOpen={() => setOpenSprintId(sprint.id)}
          />
        ))}
      </div>

      {openSprintId && (
        <SprintSummaryDrawer sprintId={openSprintId} onClose={() => setOpenSprintId(null)} />
      )}
    </div>
  );
}

function SprintCard({ sprint, onOpen }: { sprint: SprintHistoryEntry; onOpen: () => void }) {
  return (
    <article className="rounded-panel border border-line bg-surface p-4">
      <header className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="font-display text-base font-semibold">{sprint.name}</h2>
            <span className="rounded-badge border border-success-line bg-success-soft px-1.5 py-px text-[10px] font-bold tracking-[.06em] text-success-text">
              COMPLETADO
            </span>
          </div>

          <div className="mt-1 flex items-center gap-2">
            <span
              aria-hidden="true"
              className="size-2 flex-none rounded-full"
              style={{ background: sprint.project.color }}
            />
            <p className="truncate text-[13px] text-subtle">{sprint.project.name}</p>
          </div>

          <p className="mt-0.5 text-[12.5px] text-faint">
            {formatDateRange(sprint.startDate, sprint.endDate)}
          </p>

          {sprint.goal && (
            <p className="mt-2 line-clamp-2 text-[13px] text-body-soft text-pretty">{sprint.goal}</p>
          )}
        </div>
      </header>

      <div className="mt-4 flex flex-wrap items-end gap-8">
        <Metric label="Comprometido" value={sprint.committedPoints} unit="pts" />
        <Metric label="Completado" value={sprint.completedPoints} unit="pts" />
        <Metric
          label="Cumplimiento"
          value={sprint.completionRate}
          unit="%"
          tone={rateTone(sprint.completionRate)}
        />

        <button
          type="button"
          onClick={onOpen}
          className="ml-auto rounded-control border border-line-strong px-3.5 py-2 text-[13px] font-semibold transition-colors hover:border-brand hover:text-brand-deep"
        >
          Ver resumen →
        </button>
      </div>
    </article>
  );
}

function Metric({
  label,
  value,
  unit,
  tone,
}: {
  label: string;
  value: number | null;
  unit: string;
  tone?: string;
}) {
  return (
    <div>
      <p className="font-display text-[22px] font-semibold leading-none" style={tone ? { color: tone } : undefined}>
        {/* Un guion, no un cero: los sprints cerrados antes de medir esto no
            tienen la cifra, y un cero se leeria como fracaso. */}
        {value === null ? '—' : `${value} ${unit}`}
      </p>
      <p className="mt-1.5 text-[11.5px] text-subtle">{label}</p>
    </div>
  );
}

/** Verde si se cumplio, ambar si quedo cerca, rojo si se quedo lejos. */
function rateTone(rate: number | null): string | undefined {
  if (rate === null) {
    return undefined;
  }
  if (rate >= 90) {
    return '#35c48f';
  }
  if (rate >= 70) {
    return '#e0a94a';
  }
  return '#ef6f66';
}
